import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  analyticsIdentifyUser,
  analyticsLogin,
  analyticsLogout,
  analyticsMFAError,
} from '../../api/services/Analytics';
import * as AuthService from '../../api/services/Auth';
import { flushQueryWriteSync } from '../../shared/queryClient';
import { trackFullStoryEvent } from '../../vendor/features/FullStory/FullStory.service';
import { getMFAVerificationErrors, isMFASessionExpired } from '../mfaVerification';
import { HandleLogin, isAuthenticatedLogin, isMFAResponse } from '../types';

import {
  AUTH_FEATURES_QUERY_KEY,
  AUTH_IDENTITY_QUERY_KEY,
  AUTH_IMPERSONATION_QUERY_KEY,
  AUTH_USER_QUERY_KEY,
  fetchAuthIdentity,
  fetchAuthUser,
} from './useAuthQuery';
import { useMFAFlow } from './useMFAFlow';

const SESSION_ERRORS_KEY = 'authSessionErrors';

const getLoginErrors = (redirectErrors?: string[]): string[] => {
  if (redirectErrors?.length) {
    return redirectErrors;
  }
  try {
    const errors: unknown = JSON.parse(sessionStorage.getItem(SESSION_ERRORS_KEY) || '[]');

    return Array.isArray(errors) ? errors.filter((error): error is string => typeof error === 'string') : [];
  } catch {
    return [];
  }
};

// Login owns this hook's UI state; other consumers use only its session actions.
export const useAuthMutations = ({ includeSessionErrors = false }: { includeSessionErrors?: boolean } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [loginErrors, setLoginErrors] = useState<string[]>(() =>
    includeSessionErrors ? getLoginErrors(location.state?.loginErrors) : []
  );
  const [mfaErrors, setMFAErrors] = useState<string[]>([]);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const mfaFlow = useMFAFlow({
    onSessionExpired: () => {
      setMFAErrors([]);
      setLoginErrors(['Your verification session has expired. Please sign in again.']);
    },
    onDeliverySuccess: () => setMFAErrors([]),
    onReturnToChoice: () => setMFAErrors([]),
  });
  const { userEmail, setShowMFAModal } = mfaFlow;

  const loginMutation = useMutation({
    mutationFn: ({ email, password, signal }: { email: string; password: string; signal: AbortSignal }) =>
      AuthService.login(email, password, signal),
  });
  const mfaMutation = useMutation({
    mutationFn: ({ code, signal }: { code: string; signal: AbortSignal }) => AuthService.loginMFA(code, signal),
  });
  const logoutMutation = useMutation({
    mutationFn: () => AuthService.logout(),
  });
  const stopImpersonationMutation = useMutation({
    mutationFn: () => AuthService.stopImpersonation(),
  });

  const clearAuthErrors = () => {
    sessionStorage.removeItem(SESSION_ERRORS_KEY);
    setIsAccountLocked(false);
    setLoginErrors([]);
    setMFAErrors([]);
  };

  const endSession = (errors?: string[]) => {
    // A competing protected-route redirect can replace router state before Login mounts.
    if (errors?.length) {
      sessionStorage.setItem(SESSION_ERRORS_KEY, JSON.stringify(errors));
    } else if (!errors) {
      sessionStorage.removeItem(SESSION_ERRORS_KEY);
    }
    void queryClient.cancelQueries({ queryKey: [AUTH_USER_QUERY_KEY] });
    void queryClient.cancelQueries({ queryKey: [AUTH_FEATURES_QUERY_KEY] });
    void queryClient.cancelQueries({ queryKey: [AUTH_IDENTITY_QUERY_KEY] });
    flushQueryWriteSync(() => {
      queryClient.setQueryData([AUTH_USER_QUERY_KEY], null);
      queryClient.setQueryData([AUTH_FEATURES_QUERY_KEY], null);
      queryClient.setQueryData([AUTH_IDENTITY_QUERY_KEY], null);
      queryClient.setQueryData([AUTH_IMPERSONATION_QUERY_KEY], false);
      if (errors) {
        navigate('/login', { state: { loginErrors: errors } });
      } else {
        navigate('/login');
      }
    });
  };

  async function completeLogin(response, source: 'login' | 'mfa', signal: AbortSignal) {
    if (!mfaFlow.isCurrentAttempt(signal)) {
      return;
    }
    if (response?.type === 'success' && isMFAResponse(response.data)) {
      return;
    }

    if (response?.type === 'success' && !isAuthenticatedLogin(response.data)) {
      setLoginErrors(['Unable to complete sign-in. Please try again.']);

      return;
    }

    if (response?.type === 'success') {
      clearAuthErrors();

      const user = await fetchAuthUser();

      if (!mfaFlow.isCurrentAttempt(signal)) {
        return;
      }
      // Fetched and flushed before navigating so useCsrf's X-CSRF-TOKEN header is populated
      // before any route mounted at the destination can fire a non-GET request.
      const identity = user ? await fetchAuthIdentity() : null;

      if (!mfaFlow.isCurrentAttempt(signal)) {
        return;
      }

      flushQueryWriteSync(() => {
        queryClient.setQueryData<User | null>([AUTH_USER_QUERY_KEY], user);
        queryClient.setQueryData<Identity | null>([AUTH_IDENTITY_QUERY_KEY], identity);
      });

      if (user) {
        analyticsIdentifyUser(user);
        analyticsLogin();
        trackFullStoryEvent('user_login', { login_method: 'password', organization_type: user.accountType });
      }

      navigate(location.state?.path || '/');
    } else {
      if (response.status && response.status.toString().match(/(^3)/)) {
        window.location.href = response.data?.location || '';
      } else {
        if (response.status === 423) {
          setIsAccountLocked(true);
        }

        if (source === 'mfa') {
          setMFAErrors(response.error.errors);
        } else if (source === 'login') {
          setLoginErrors(response.error.errors);
        }
      }
    }
  }

  const handleLogin: HandleLogin = async ({ email, password }, resetField) => {
    const signal = mfaFlow.beginLogin(email);

    clearAuthErrors();
    const response = await loginMutation.mutateAsync({ email, password, signal });

    if (!mfaFlow.isCurrentAttempt(signal)) {
      return;
    }

    if (response?.type === 'success' && isMFAResponse(response.data)) {
      resetField('password');
      mfaFlow.openMFA(response.data);
    } else {
      await completeLogin(response, 'login', signal);
    }
  };

  const handleMFA = async (code: string) => {
    if (isAccountLocked) {
      return;
    }
    const signal = mfaFlow.beginVerification(code);

    if (!signal) {
      return;
    }
    setMFAErrors([]);
    try {
      const response = await mfaMutation.mutateAsync({ code, signal });

      if (!mfaFlow.isCurrentAttempt(signal)) {
        return;
      }
      if (isMFASessionExpired(response)) {
        setShowMFAModal(false);
        setLoginErrors(['Your verification session has expired. Please sign in again.']);

        return;
      }
      if (response.type === 'error') {
        const messages = response.error.errors;

        analyticsMFAError(
          userEmail,
          messages.some((error) => error.includes('incorrect code')),
          messages.some((error) => error.includes('expired')),
          mfaFlow.mfaStep?.mfa?.state === 'code_sent' ? mfaFlow.mfaStep.mfa.method : 'email'
        );
        setIsAccountLocked(response.status === 423);
        setMFAErrors(getMFAVerificationErrors(response));

        return;
      }
      if (!isAuthenticatedLogin(response.data)) {
        setMFAErrors(getMFAVerificationErrors(response));

        return;
      }
      await completeLogin(response, 'mfa', signal);
      if (mfaFlow.isCurrentAttempt(signal)) {
        setShowMFAModal(false);
      }
    } catch {
      if (mfaFlow.isCurrentAttempt(signal)) {
        setMFAErrors(['Unable to verify your code. Please try again.']);
      }
    } finally {
      mfaFlow.finishVerification(signal);
    }
  };

  const handleLogout = async () => {
    setShowMFAModal(false);
    analyticsLogout();
    await logoutMutation.mutateAsync();

    location.state = { logout: true };
    endSession();
  };

  const handleStopImpersonation = async () => {
    analyticsLogout();
    await stopImpersonationMutation.mutateAsync();

    location.state = { logout: true };
    endSession();
  };

  const handleSessionUnauthorized = (errors: string[] = []) => {
    setShowMFAModal(false);
    endSession(errors);
  };

  return {
    loginErrors,
    setLoginErrors,
    mfaErrors,
    setMFAErrors,
    isAccountLocked,
    ...mfaFlow,
    handleLogin,
    handleMFA,
    handleLogout,
    handleStopImpersonation,
    handleSessionUnauthorized,
  };
};
